import { z } from 'zod';
export const AppEnvironmentSchema = z.enum(['development', 'test', 'staging', 'production']);
const allowedTestHosts = ['localhost', '127.0.0.1', 'mongo', 'redis'];
function assertSafeDatabaseTarget(uri, nodeEnv) {
    if (nodeEnv === 'production') {
        throw new Error('Integration tests must not run with NODE_ENV=production');
    }
    let hostname;
    try {
        hostname = new URL(uri).hostname;
    }
    catch {
        throw new Error(`Invalid MongoDB URI: ${uri}`);
    }
    if (!allowedTestHosts.includes(hostname)) {
        throw new Error(`Refusing to connect to non-local MongoDB host "${hostname}". Use localhost or CI service names only.`);
    }
}
const baseEnvSchema = z.object({
    NODE_ENV: AppEnvironmentSchema.default('development'),
    APP_ENV: AppEnvironmentSchema.default('development'),
    APP_NAME: z.string().default('soroush-airline'),
    APP_VERSION: z.string().default('0.0.0'),
    LOG_LEVEL: z.enum(['debug', 'info', 'warn', 'error']).default('info'),
    MONGODB_URI: z.string().min(1),
    REDIS_URL: z.string().min(1),
    REDIS_KEY_PREFIX: z.string().default(''),
    API_HOST: z.string().default('0.0.0.0'),
    API_PORT: z.coerce.number().int().positive().default(4000),
    SHUTDOWN_TIMEOUT_MS: z.coerce.number().int().positive().default(30000),
    GRACEFUL_SHUTDOWN_ENABLED: z
        .enum(['true', 'false'])
        .default('true')
        .transform((v) => v === 'true'),
});
export const ApiEnvSchema = baseEnvSchema.extend({
    JWT_SECRET: z.string().min(32),
    JWT_ACCESS_TTL: z.string().default('15m'),
    JWT_REFRESH_TTL_DAYS: z.coerce.number().int().positive().default(7),
    CORS_ORIGIN: z.string().default('http://localhost:5173'),
    BOOKING_HOLD_MINUTES: z.coerce.number().int().positive().default(15),
    PAYMENT_GATEWAY: z.enum(['MOCK', 'SAMAN']).default('MOCK'),
    PAYMENT_CALLBACK_URL: z.string().url().optional(),
    WEB_BASE_URL: z.string().url().optional(),
    NIRA_BASE_URL: z.string().url().optional(),
    NIRA_API_KEY: z.string().optional(),
    NRS_BASE_URL: z.string().url().optional(),
    NRS_OFFICE_USER: z.string().optional(),
    NRS_OFFICE_PASS: z.string().optional(),
    NRS_TLS_INSECURE: z
        .enum(['true', 'false'])
        .default('false')
        .transform((v) => v === 'true'),
    NRS_WS2_URL: z.string().url().optional(),
    NRS_AIRLINE: z.string().min(1).default('*'),
    NRS_TIMEOUT_MS: z.coerce.number().int().positive().default(20000),
    NRS_SALES_ENABLED: z
        .enum(['true', 'false'])
        .default('false')
        .transform((v) => v === 'true'),
    NRS_TICKETING_MAX_ATTEMPTS: z.coerce.number().int().positive().default(8),
    NRS_REST_BASE_URL: z.string().url().optional(),
    NRS_API_KEY: z.string().optional(),
    NRS_OFFICE_CODE: z.string().optional(),
    BCRYPT_ROUNDS: z.coerce.number().int().min(10).max(14).default(12),
    MAX_LOGIN_ATTEMPTS: z.coerce.number().int().positive().default(5),
    LOCKOUT_MINUTES: z.coerce.number().int().positive().default(15),
});
export const WorkerEnvSchema = baseEnvSchema.extend({
    WORKER_CONCURRENCY: z.coerce.number().int().positive().default(5),
    QUEUE_NAME: z.string().default('default'),
});
export const ReconcilerEnvSchema = baseEnvSchema.extend({
    RECONCILIATION_CRON: z.string().default('0 */6 * * *'),
    BOOKING_SYNC_CRON: z.string().default('* * * * *'),
    RECONCILIATION_ENABLED: z
        .enum(['true', 'false'])
        .default('true')
        .transform((v) => v === 'true'),
});
export function parseEnv(schema, source = process.env) {
    const parsed = schema.safeParse(source);
    if (!parsed.success) {
        const details = parsed.error.issues
            .map((issue) => `${issue.path.join('.')}: ${issue.message}`)
            .join('; ');
        throw new Error(`Environment validation failed: ${details}`);
    }
    return parsed.data;
}
export function assertTestDatabaseSafety(env) {
    if (env.NODE_ENV === 'test') {
        assertSafeDatabaseTarget(env.MONGODB_URI, env.NODE_ENV);
    }
}
export function createTestRunId() {
    return `${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
}
export function buildTestMongoUri(baseUri, runId) {
    const url = new URL(baseUri);
    url.pathname = `/ibe_test_${runId}`;
    return url.toString();
}
export function buildTestRedisPrefix(runId) {
    return `test:${runId}:`;
}
//# sourceMappingURL=env.js.map